import React, { useEffect } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getAccessUsers, loadMoreAccessUsers } from '../../../redux/projects/projectsActions';

import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import WithError from '../../../components/with-error/WithError';
import { Input } from '../../../components/common-inputs';
import SearchInput from '../../../components/common-inputs/SearchInput';
import AccessContainers from '../../../components/access-containers/AccessContainers';
import TooltipButton from '../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../components/common-button/CommonButton';

import scss from './Activ8Creation.module.scss';

function Activ8CreationForm({ handleSubmit }) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { restart, change } = useForm();
  const { values } = useFormState();

  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const isLoading = useSelector((state) => state.projects.isLoading);

  const { accessUsers, totalAccessUsers } = useSelector((state) => state.projects);

  const inactiveUsers = accessUsers.filter(
    (user) => !values.activeUsers.some((activeUser) => activeUser.id === user.id),
  );

  const handleRestForm = () => {
    restart();
    navigate('/projects/create');
  };

  const handleActivateUser = (user) => {
    change('activeUsers', [...values.activeUsers, user]);
  };

  const handleInactivateUser = ({ id }) => {
    change(
      'activeUsers',
      values.activeUsers.filter((user) => user.id !== id),
    );
  };

  const handleSearchUsers = (newSearch) => {
    change('assignUser', newSearch);
    dispatch(getAccessUsers({ search: newSearch, page: 1 }));
  };

  const handleLoadMoreUsers = (newPage) => {
    dispatch(loadMoreAccessUsers({ search: values.assignUser ?? '', page: newPage }));
  };

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='projectName' autoComplete='project-name'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.CUSTOM} label={`${t('projectName')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='project-name'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='assignUser'>
        {({ input }) => (
          <WithLabel label={t('assignUser')} variant={LABEL_STYLES.CUSTOM} isBold extraClassName={scss.inputRow}>
            <SearchInput
              value={input.value}
              onChange={handleSearchUsers}
              name={input.name}
              size='sm'
              disabled={isLoading}
            />
          </WithLabel>
        )}
      </Field>

      <Field name='activeUsers'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <AccessContainers
              onInactive={handleInactivateUser}
              onActive={handleActivateUser}
              inactive={inactiveUsers}
              active={input.value}
              disabled={isLoading}
              isLoading={isAccessUsersLoading}
              searchStr={values.assignUser}
              onLoadMore={handleLoadMoreUsers}
              totalElements={totalAccessUsers}
              inactiveLoadedSize={accessUsers.length}
            />
          </WithError>
        )}
      </Field>

      <div className={scss.actions}>
        <TooltipButton
          type='submit'
          styleColor={BUTTON_STYLES.PRIMARY}
          onClick={handleSubmit}
          maxWidth={100}
          isLoading={isLoading}
          disabled={isLoading}>
          {t('create')}
        </TooltipButton>
        <TooltipButton
          type='button'
          styleColor={BUTTON_STYLES.SECONDARY}
          onClick={handleRestForm}
          maxWidth={100}
          disabled={isLoading}>
          {t('cancel')}
        </TooltipButton>
      </div>
    </form>
  );
}

export default Activ8CreationForm;
